/**
 * Telegram long-poll ingest for the opencode runtime (ADR-0047).
 *
 * On Claude Code the telegram-channel MCP owns the `getUpdates` poll loop and
 * pushes inbound straight into the session. opencode has no in-session channel
 * MCP, so the MANAGER runs the poll loop, gates each raw update with the shared
 * core SenderGate (`evaluateTelegramInboundGate`), and drives the agent's
 * `opencode serve` over the inbound bridge on admit - the exact governance the
 * Claude handler applies, reused (no policy replicated here).
 *
 * The transport (getUpdates long-poll -> offset advance) and the outbound send
 * (sendMessage) mirror telegram-channel.ts. The HTTP client and the durable-offset
 * store are injectable so the dispatch logic is unit-testable without a live
 * Telegram.
 *
 * HTTP goes through `node:https` with `family: 4`, NOT global `fetch`: undici
 * cannot reach api.telegram.org on some networks (broken/absent IPv6 egress where
 * the dual-stack A+AAAA record makes fetch's Happy-Eyeballs hang on the
 * unreachable IPv6 address instead of failing over to the working IPv4). This is
 * the exact reason the manager's `telegramApiCall` helper and the Claude-path
 * telegram-channel MCP both bypass fetch. Forcing IPv4 is safe for this fleet:
 * api.telegram.org always publishes an A record and the hosts are IPv4-primary.
 *
 * Telegram is long-poll, NOT Socket Mode: there is no envelope to ack, so the
 * getUpdates cursor (`offset`) is the delivery boundary. Advancing it past an
 * update tells Telegram to stop redelivering it, so we advance BEFORE dispatch
 * (even on a drop) exactly as the MCP does - a wedged agent must not re-ask
 * Telegram for the same update forever. This is an at-most-once boundary: a crash
 * after the offset advances but before the turn completes loses that update, the
 * same tradeoff the Slack ingest documents for its pre-ack.
 *
 * Deliberately handles ONLY `message` updates. callback_query (inline keyboards),
 * edited_message, and channel_post are not driven through this path yet.
 */

import https from 'node:https';

import {
  evaluateTelegramInboundGate,
  type TelegramGateEvent,
} from '@augmented/core/channels/governance/telegram-sender-gate.js';
import type { PeerClassifierConfig, PeerClassifierSelf } from '@augmented/core/channels/governance/telegram-peer-classifier.js';
import {
  buildTelegramPeerClassifierConfigFromEnv,
  parseTelegramAllowedChatsEnv,
  type EnvLike,
} from '@augmented/core/channels/governance/telegram-inbound-config.js';
import type {
  InboundMessage,
  InboundResult,
} from '@augmented/core/provisioning/frameworks/opencode/index.js';

/** Minimal fetch surface (avoids a DOM-lib dependency). */
export interface MinimalJsonResponse {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}
export type FetchLike = (
  url: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string; signal?: AbortSignal },
) => Promise<MinimalJsonResponse>;

/**
 * The default HTTP client: `node:https` with `family: 4`, mirroring the manager's
 * `telegramApiCall`. See the module header for why fetch is unusable here. Honors
 * an `AbortSignal` (so a long-poll aborts on stop) and a socket-inactivity
 * timeout above the long-poll hold as a backstop. Returns the `MinimalJsonResponse`
 * shape the transport expects (`status` for auth-failure detection, `json()` for
 * the Telegram envelope).
 */
export const nodeHttpsTelegramFetch: FetchLike = (url, init) =>
  new Promise<MinimalJsonResponse>((resolve, reject) => {
    const u = new URL(url);
    const body = init?.body;
    const headers: Record<string, string> = { ...(init?.headers ?? {}) };
    if (body !== undefined && headers['Content-Length'] === undefined) {
      headers['Content-Length'] = String(Buffer.byteLength(body));
    }
    const req = https.request(
      {
        hostname: u.hostname,
        port: 443,
        path: u.pathname + u.search,
        method: init?.method ?? 'GET',
        family: 4,
        // Backstop above the long-poll hold (getUpdates parks up to
        // LONG_POLL_SECONDS); short calls carry their own AbortSignal.timeout.
        timeout: POLL_HTTP_TIMEOUT_MS + 5_000,
        headers,
      },
      (res) => {
        let data = '';
        res.on('data', (d) => {
          data += d;
        });
        res.on('end', () => {
          const status = res.statusCode ?? 0;
          resolve({
            ok: status >= 200 && status < 300,
            status,
            json: async () => JSON.parse(data.length > 0 ? data : '{}'),
          });
        });
      },
    );
    req.on('error', reject);
    req.on('timeout', () => {
      req.destroy(new Error('Telegram API socket timeout'));
    });
    const signal = init?.signal;
    if (signal) {
      if (signal.aborted) req.destroy(new Error('aborted'));
      else signal.addEventListener('abort', () => req.destroy(new Error('aborted')), { once: true });
    }
    if (body !== undefined) req.write(body);
    req.end();
  });

const TELEGRAM_API = 'https://api.telegram.org';
/** Long-poll hold (seconds) - mirrors telegram-channel.ts LONG_POLL_SECONDS. */
const LONG_POLL_SECONDS = 50;
/** HTTP timeout must exceed the long-poll hold plus a margin. */
const POLL_HTTP_TIMEOUT_MS = (LONG_POLL_SECONDS + 10) * 1000;
/** Backoff after a getUpdates failure so a revoked token can't hot-loop. */
const POLL_ERROR_BACKOFF_MS = 5_000;

/** The subset of the Telegram Bot API `Message` the ingest reads. */
export interface TelegramMessage extends TelegramGateEvent {
  message_thread_id?: number;
}

interface TelegramUpdate {
  update_id: number;
  message?: TelegramMessage;
}

interface TelegramApiEnvelope<T> {
  ok: boolean;
  result?: T;
  description?: string;
}

/**
 * Durable getUpdates cursor. Injected so the transport stays testable (an
 * in-memory store) while production seeds/persists it to disk across manager
 * restarts (`createFileOffsetStore`). Both methods fail safe in the file impl.
 */
export interface TelegramOffsetStore {
  load(): number;
  save(offset: number): void;
}

/** In-memory offset store (test default): no cross-restart durability. */
export function memoryOffsetStore(initial = 0): TelegramOffsetStore {
  let value = initial;
  return { load: () => value, save: (o) => { value = o; } };
}

/**
 * The Telegram gate + transport parameters an opencode agent's ingest needs,
 * derived from its materialized Telegram env (Brad's option (a): the manager
 * materializes the same env contract the Claude MCP uses via
 * `buildChannelCredentialEnv` + `buildChannelServerEnv`, and this turns it into
 * ingest params).
 */
export interface TelegramIngestParams {
  botToken: string;
  allowedChats: Set<string>;
  peerConfig: PeerClassifierConfig;
  /** Emoji added on admit ("seen, working on it"); from TELEGRAM_ACK_REACTION. */
  ackReaction?: string;
  /** Emoji added on an admitted no-reply ("seen, no action"); from TELEGRAM_SKIP_REACTION. */
  skipReaction?: string;
}

/**
 * Resolve the ingest's gate + transport params from a materialized Telegram env.
 * Returns null when polling is impossible (no bot token) so the caller simply
 * skips starting the loop.
 */
export function resolveTelegramIngestParamsFromEnv(env: EnvLike): TelegramIngestParams | null {
  const botToken = env['TELEGRAM_BOT_TOKEN'];
  if (!botToken) return null;
  // Default the ack reaction to 👀 when unset - byte-identical to the claude-path
  // telegram-channel MCP's `ACK_EMOJI` fallback, so an opencode agent gets the
  // same "seen, working on it" ack even when the channel config carries no
  // explicit ack_reaction. skip_reaction stays opt-in (no MCP default; it is
  // flag-gated MCP-side), so it is only emitted when configured.
  const ackReaction = (env['TELEGRAM_ACK_REACTION'] ?? '').trim() || '👀';
  const skipReaction = (env['TELEGRAM_SKIP_REACTION'] ?? '').trim() || undefined;
  return {
    botToken,
    allowedChats: parseTelegramAllowedChatsEnv(env),
    peerConfig: buildTelegramPeerClassifierConfigFromEnv(env),
    ackReaction,
    ...(skipReaction ? { skipReaction } : {}),
  };
}

/**
 * Telegram Bot API error codes meaning the token is DEFINITIVELY invalid (as
 * opposed to a transient blip). Telegram signals auth failure with HTTP 401 and
 * `description` containing "Unauthorized"; we key on that.
 */
function isTelegramAuthFailure(status: number, description: string | undefined): boolean {
  return status === 401 || (description ?? '').toLowerCase().includes('unauthorized');
}

/**
 * Resolve our own bot identity via `getMe`, for peer-agent classification and
 * self-echo detection. `authFailed` separates a revoked token (stop, surface it)
 * from a transient failure (worth another attempt). Never throws: an unresolved
 * identity degrades peer classification (bots get `self_resolution_pending`)
 * without blocking the human path.
 */
export async function resolveTelegramBotIdentity(
  botToken: string,
  fetchImpl?: FetchLike,
): Promise<{ self: PeerClassifierSelf; authFailed: boolean }> {
  const doFetch = fetchImpl ?? nodeHttpsTelegramFetch;
  const unresolved: PeerClassifierSelf = { bot_id: null, bot_username: null };
  try {
    const res = await doFetch(`${TELEGRAM_API}/bot${botToken}/getMe`, {
      method: 'POST',
      signal: AbortSignal.timeout(10_000),
    });
    const data = (await res.json()) as TelegramApiEnvelope<{ id: number; username?: string }>;
    if (data.ok && data.result) {
      return {
        self: {
          bot_id: data.result.id,
          // Lowercased to match resolveBotUsername()'s cache convention.
          bot_username: data.result.username ? data.result.username.toLowerCase() : null,
        },
        authFailed: false,
      };
    }
    return { self: unresolved, authFailed: isTelegramAuthFailure(res.status, data.description) };
  } catch {
    // Network / parse failure - transient, NOT a definitive auth failure.
    return { self: unresolved, authFailed: false };
  }
}

export type TelegramReplySender = (args: {
  chatId: string;
  text: string;
  messageThreadId?: number;
}) => Promise<void>;

/** Adds an emoji reaction to a message (fire-and-forget; failures logged, never thrown). */
export type TelegramReactionAdder = (args: {
  chatId: string;
  messageId: number;
  emoji: string;
}) => Promise<void>;

export interface TelegramIngestConfig {
  codeName: string;
  agentId: string;
  botToken: string;
  allowedChats: Set<string>;
  peerConfig: PeerClassifierConfig;
  /** Our own bot identity (getMe), for peer classification + self detection. */
  self: PeerClassifierSelf;
  /** Inject an admitted, normalized message into the agent's opencode server. */
  inject: (msg: InboundMessage) => Promise<InboundResult>;
  log: (m: string) => void;
  /** Durable getUpdates cursor (defaults to in-memory). */
  offsetStore?: TelegramOffsetStore;
  ackReaction?: string;
  skipReaction?: string;
  /** Test seams. */
  fetchImpl?: FetchLike;
  sendReply?: TelegramReplySender;
  addReaction?: TelegramReactionAdder;
}

/** What the dispatcher decided for one inbound update (returned for tests/telemetry). */
export type TelegramDispatchOutcome =
  | 'dropped_duplicate'
  | 'dropped_gate'
  | 'declined'
  | 'admitted_no_reply'
  | 'admitted_replied';

interface DispatchCtx {
  codeName: string;
  allowedChats: Set<string>;
  peerConfig: PeerClassifierConfig;
  self: PeerClassifierSelf;
  inject: (msg: InboundMessage) => Promise<InboundResult>;
  sendReply: TelegramReplySender;
  addReaction: TelegramReactionAdder;
  ackReaction?: string;
  skipReaction?: string;
  log: (m: string) => void;
  /** Bounded update_id dedup for poller-handoff / overlap redelivery. */
  seen: BoundedSet;
}

/** Insertion-ordered set with a hard cap so redelivery dedup can't grow unbounded. */
export class BoundedSet {
  private readonly max: number;
  private readonly set = new Set<string>();
  constructor(max = 500) {
    this.max = max;
  }
  has(k: string): boolean {
    return this.set.has(k);
  }
  add(k: string): void {
    this.set.add(k);
    if (this.set.size > this.max) {
      const oldest = this.set.values().next().value as string | undefined;
      if (oldest !== undefined) this.set.delete(oldest);
    }
  }
}

/**
 * Dispatch one Telegram message: dedup -> gate -> ack -> inject -> reply. Pure of
 * transport (no polling, no offset). Returns the outcome.
 */
export async function dispatchTelegramInbound(
  update: { update_id: number; message: TelegramMessage },
  ctx: DispatchCtx,
): Promise<TelegramDispatchOutcome> {
  const msg = update.message;
  const chatId = String(msg.chat.id);

  // 1. Redelivery dedup: a poller-handoff window (two loops briefly overlapping
  //    across a restart) can surface the same update twice. Drop a repeat
  //    update_id we have already processed this process-life.
  const dedupKey = String(update.update_id);
  if (ctx.seen.has(dedupKey)) return 'dropped_duplicate';
  ctx.seen.add(dedupKey);

  // 2. Governance gate (content, org boundary, peer classification).
  const decision = evaluateTelegramInboundGate(msg, {
    allowedChats: ctx.allowedChats,
    peerConfig: ctx.peerConfig,
    self: ctx.self,
  });
  if (!decision.admit) {
    ctx.log(`[telegram-ingest:${ctx.codeName}] dropped chat=${redactId(chatId)} reason=${decision.reason}`);
    return 'dropped_gate';
  }

  // Admitted: acknowledge receipt with the "seen" reaction. Fire-and-forget so
  // it never blocks the inject or fails the turn (parity with the claude
  // telegram-channel MCP; opencode has no in-session MCP, so the manager owns it).
  if (ctx.ackReaction) {
    void ctx.addReaction({ chatId, messageId: msg.message_id, emoji: ctx.ackReaction });
  }

  // 3. Normalize + inject. A chat (and forum topic) maps to a stable conversation
  //    key so the opencode session keeps that conversation's context across turns.
  const conversationKey = msg.message_thread_id ? `${chatId}:${msg.message_thread_id}` : chatId;
  const meta: Record<string, string> = {
    source: 'telegram',
    lane: 'conversational',
    requires_reply: 'true',
    chat_id: chatId,
  };
  if (msg.message_thread_id) meta['message_thread_id'] = String(msg.message_thread_id);

  const result = await ctx.inject({
    channelId: 'telegram',
    conversationKey,
    senderId: msg.from ? String(msg.from.id) : 'unknown',
    text: msg.text ?? msg.caption ?? '',
    meta,
  });

  if (result.status === 'declined') {
    ctx.log(`[telegram-ingest:${ctx.codeName}] inject declined reason=${result.reason}`);
    return 'declined';
  }
  // 4. Post the reply back in the same chat (and topic).
  if (result.status === 'replied' && result.reply) {
    await ctx.sendReply({
      chatId,
      text: result.reply,
      ...(msg.message_thread_id ? { messageThreadId: msg.message_thread_id } : {}),
    });
    ctx.log(`[telegram-ingest:${ctx.codeName}] outcome=replied chat=${redactId(chatId)} len=${result.reply.length}`);
    return 'admitted_replied';
  }
  // Admitted but no reply produced: mark it "seen, no action". Log the raw inject
  // status so an empty turn (status='replied', reply=null) is distinguishable from
  // a durable-admit-without-read (status='admitted') - the two look identical on
  // the wire (silence) but mean different things when a turn "never replied".
  ctx.log(
    `[telegram-ingest:${ctx.codeName}] outcome=no_reply chat=${redactId(chatId)} inject_status=${result.status}`,
  );
  if (ctx.skipReaction) {
    void ctx.addReaction({ chatId, messageId: msg.message_id, emoji: ctx.skipReaction });
  }
  return 'admitted_no_reply';
}

/** Redact a Telegram id for durable logs (keep the leading char / sign). */
function redactId(id: string): string {
  if (!id) return '?';
  return `${id.slice(0, 2)}***`;
}

/** Handle to a running ingest loop. */
export interface TelegramIngestHandle {
  stop(): void;
}

/** Default outbound: sendMessage via the bot token. */
function defaultSendReply(botToken: string, fetchImpl: FetchLike, log: (m: string) => void): TelegramReplySender {
  return async ({ chatId, text, messageThreadId }) => {
    try {
      const res = await fetchImpl(`${TELEGRAM_API}/bot${botToken}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          text,
          ...(messageThreadId ? { message_thread_id: messageThreadId } : {}),
        }),
        // Dedicated short timeout so an outbound reply can't inherit the ~50s
        // long-poll getUpdates timeout and hang a turn on a slow Telegram edge.
        signal: AbortSignal.timeout(10_000),
      });
      const data = (await res.json()) as TelegramApiEnvelope<unknown>;
      if (!data.ok) log(`[telegram-ingest] sendMessage failed: ${data.description}`);
    } catch (err) {
      log(`[telegram-ingest] sendMessage threw: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
}

/** Default reaction: setMessageReaction via the bot token. Fire-and-forget. */
function defaultAddReaction(botToken: string, fetchImpl: FetchLike, log: (m: string) => void): TelegramReactionAdder {
  return async ({ chatId, messageId, emoji }) => {
    try {
      const res = await fetchImpl(`${TELEGRAM_API}/bot${botToken}/setMessageReaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          message_id: messageId,
          reaction: [{ type: 'emoji', emoji }],
        }),
        // Dedicated short timeout — the ack must not inherit the long-poll window.
        signal: AbortSignal.timeout(10_000),
      });
      const data = (await res.json()) as TelegramApiEnvelope<unknown>;
      // A rejected reaction emoji (Telegram allows only a fixed set) is benign -
      // log once, never throw. The turn already proceeds without the ack.
      if (!data.ok) log(`[telegram-ingest] setMessageReaction failed: ${data.description}`);
    } catch (err) {
      log(`[telegram-ingest] setMessageReaction threw: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms).unref?.();
  });
}

/**
 * Start the long-poll ingest loop for one opencode agent. Seeds the cursor from
 * the durable offset store, drains each getUpdates batch through
 * `dispatchTelegramInbound`, and persists the cursor once per non-empty batch,
 * until stopped.
 */
export function startTelegramIngest(config: TelegramIngestConfig): TelegramIngestHandle {
  const fetchImpl = config.fetchImpl ?? nodeHttpsTelegramFetch;
  const sendReply = config.sendReply ?? defaultSendReply(config.botToken, fetchImpl, config.log);
  const addReaction = config.addReaction ?? defaultAddReaction(config.botToken, fetchImpl, config.log);
  const offsetStore = config.offsetStore ?? memoryOffsetStore();
  const seen = new BoundedSet();

  const ctx: DispatchCtx = {
    codeName: config.codeName,
    allowedChats: config.allowedChats,
    peerConfig: config.peerConfig,
    self: config.self,
    inject: config.inject,
    sendReply,
    addReaction,
    ...(config.ackReaction ? { ackReaction: config.ackReaction } : {}),
    ...(config.skipReaction ? { skipReaction: config.skipReaction } : {}),
    log: config.log,
    seen,
  };

  let stopped = false;
  let controller: AbortController | null = null;
  let nextOffset = offsetStore.load();

  const pollLoop = async (): Promise<void> => {
    while (!stopped) {
      try {
        controller = new AbortController();
        // Bound the request above the long-poll hold; abort on stop() so a
        // shutdown doesn't wait out the full hold.
        const timeout = AbortSignal.timeout(POLL_HTTP_TIMEOUT_MS);
        const signal = anySignal([controller.signal, timeout]);
        const res = await fetchImpl(`${TELEGRAM_API}/bot${config.botToken}/getUpdates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            offset: nextOffset,
            timeout: LONG_POLL_SECONDS,
            allowed_updates: ['message'],
          }),
          signal,
        });
        if (stopped) return;
        const data = (await res.json()) as TelegramApiEnvelope<TelegramUpdate[]>;
        if (!data.ok) {
          config.log(`[telegram-ingest:${config.codeName}] getUpdates failed: ${data.description ?? 'unknown'}`);
          await sleep(POLL_ERROR_BACKOFF_MS);
          continue;
        }

        const updates = data.result ?? [];
        for (const update of updates) {
          // Advance the cursor BEFORE dispatch (even on a drop) so a wedged turn
          // never re-asks Telegram for the same update.
          nextOffset = Math.max(nextOffset, update.update_id + 1);
          const msg = update.message;
          if (!msg) continue;
          try {
            await dispatchTelegramInbound({ update_id: update.update_id, message: msg }, ctx);
          } catch (err) {
            config.log(
              `[telegram-ingest:${config.codeName}] dispatch threw: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        }
        // Persist at most once per non-empty batch, keeping the healthy path cheap.
        if (updates.length > 0) offsetStore.save(nextOffset);
      } catch (err) {
        if (stopped) return;
        // An aborted getUpdates (stop) lands here too; the stopped guard above
        // catches the intentional case, so anything reaching here is a real
        // transport error worth a backoff.
        config.log(`[telegram-ingest:${config.codeName}] poll threw: ${err instanceof Error ? err.message : String(err)}`);
        await sleep(POLL_ERROR_BACKOFF_MS);
      }
    }
  };

  void pollLoop();

  return {
    stop() {
      stopped = true;
      try {
        controller?.abort();
      } catch {
        /* ignore */
      }
    },
  };
}

/** Combine abort signals (Node 20 lacks AbortSignal.any in all minors). */
function anySignal(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();
  for (const s of signals) {
    if (s.aborted) {
      controller.abort();
      break;
    }
    s.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return controller.signal;
}
